import { useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import config from '../configLoader';
import { defaultAdminEmployeeRole, useSiteContext } from '../SiteContext';
import { LoginResponse } from '../interfaces/LayoutInterface';
import LoginManager from '../managers/LoginManager';
import LocationsManager from '../managers/LocationsManager';
import { getPendingTimeOffCount } from '../api/timeOffRequests';
import {
  AdminPermission,
  can as canPermission,
  isLimitedUser,
  getDefaultPageForLimitedUser,
} from '../util/adminPermissions';
import { Page } from '../interfaces/PagesInterface';
import { readStoredExperience, resolveExperience, safeStorage } from '../Pages/v2/employeePortal/portalSession';

const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000;

export interface AdminSession {
  /** True only for a user with admin access (the admin shell is mounted). */
  loggedIn: boolean;
  /** Signed in with employee access but no admin role (TAS-667). The admin shell
   *  stays unmounted; the layout shows the Employee Time Portal (P-TAS-20 Phase 3) where the
   *  tenant enables it, else the "coming soon" card. */
  employeeOnly: boolean;
  /** Why the last sign-in attempt failed (server errorMessage), for the sign-in card. */
  loginError: string | null;
  /** MSAL has an account, so "Sign out" (switch account) is meaningful. */
  isAuthenticated: boolean;
  handleLogin: () => Promise<void>;
  handleLogout: () => void;
  /** Centralized capability check for the current session's role (admin implies all). */
  can: (permission: AdminPermission) => boolean;
}

/**
 * Auth/session logic extracted from Layout.tsx (MSAL popup login → POST /admin/login,
 * 2h inactivity timer, limited-user default page, deep-link consumption, loads
 * locations/miscTaskApprovalConfigs/pendingTimeOffCount). All side effects flow through
 * SiteContext setters exactly as before.
 */
export function useAdminSession(): AdminSession {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { userRole, setUserName, setUserRole, setError, setUserID, setSelfEmployeeID, setSupervisorQueryID, setLoading, setPage, createPopup, setSuperAltID, setLocations, setMiscTaskApprovalConfigs, pendingDeepLink, setPendingDeepLink, setPendingTimeOffCount, setHasAdminAccess, setHasEmployeeAccess, setExperience, setPortalBadges, setSelfRoleName } = useSiteContext();
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [employeeOnly, setEmployeeOnly] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  // Bumped by the inactivity timeout so the session is re-validated with the server for
  // employee-only users too (their loggedIn never changes, so the login effect would not rerun).
  const [idleNonce, setIdleNonce] = useState<number>(0);
  const portalEnabled = !!config.employeePortal?.enabled;

  /** Drop everything a previous sign-in put in the session (role, ids, flags). */
  const clearSessionState = () => {
    setLoggedIn(false);
    setEmployeeOnly(false);
    setUserRole(defaultAdminEmployeeRole);
    setUserID(0);
    setSelfEmployeeID(null);
    setMiscTaskApprovalConfigs([]);
    setHasAdminAccess(false);
    setHasEmployeeAccess(false);
    setExperience('admin', false);
    setPortalBadges({ timesheet: 0, timeOff: 0 });
    setSelfRoleName(null);
  };

  /** The inactivity timeout: drops the admin AND the employee-only session (P3-B), then re-validates. */
  const expireIdleSession = () => {
    setLoggedIn(false);
    setEmployeeOnly(false);
    setIdleNonce((n) => n + 1);
  };

  let timeoutId: NodeJS.Timeout | null = null;

  // Reset inactivity timer
  const resetIdleTimer = () => {
    const currentTime = new Date().getTime();
    localStorage.setItem('lastActivityTime', currentTime.toString());

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      expireIdleSession();
    }, INACTIVITY_TIMEOUT);
  };

  // Handle logout
  const handleLogout = () => {
    if (instance) {
      instance.logoutPopup().catch((error) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        console.error('Logout failed:', errorMessage);
        createPopup('Logout failed:', errorMessage);
        return;
      });
      clearSessionState();
      setLoginError(null);
    } else {
      console.error("MSAL instance is not initialized.");
    }
  };

  // Inactivity and login logic (unchanged)
  useEffect(() => {
    const events = ['mousemove', 'keydown', 'click', 'scroll'];

    const activityHandler = () => {
      resetIdleTimer();
    };

    events.forEach((event) => {
      window.addEventListener(event, activityHandler);
    });

    const lastActivityTime = localStorage.getItem('lastActivityTime');
    const currentTime = new Date().getTime();

    if (lastActivityTime && isAuthenticated) {
      const elapsed = currentTime - parseInt(lastActivityTime, 10);
      if (elapsed > INACTIVITY_TIMEOUT) {
        expireIdleSession();
        createPopup("Logged Out Due to Inactivity.", "Please sign in again.");
      } else {
        resetIdleTimer();
      }
    } else {
      resetIdleTimer();
    }

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, activityHandler);
      });
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [instance]);

  const loginAsync = async () => {
    setLoading(true);
    try {
      if (isAuthenticated && accounts[0]) {
        const data: LoginResponse | undefined = await LoginManager.login();
        console.log("LOGIN:", data);

        if (!data || data.success !== true) {
          // success:false carries the server's reason (not found, OID mismatch, no
          // access...). Show it on the sign-in card. No auto sign-out: the card offers
          // "Sign out" so the user can switch accounts, and nothing loops.
          clearSessionState();
          setLoginError(data?.errorMessage || 'Sign-in failed. Please try again.');
          setLoading(false);
          return;
        }

        const { adminEmployee } = data;
        // An older API has no adminAccess flag and always sent a role.
        const adminAccess = data.adminAccess ?? !!data.adminEmployeeRole;
        const role = adminAccess ? data.adminEmployeeRole ?? null : null;
        // Stored for EVERY login (P3-B): a user with both roles keeps their employee access.
        const employeeAccess = !!data.employeeAccess && !!data.selfEmployee;
        setSelfEmployeeID(data.selfEmployee?.employeeID ?? null);
        setSelfRoleName(data.selfEmployee?.roleName ?? null);
        setHasEmployeeAccess(employeeAccess);
        setHasAdminAccess(!!(adminAccess && role && adminEmployee));
        setLoginError(null);

        if (!adminAccess || !role || !adminEmployee) {
          setLoggedIn(false);
          setUserRole(defaultAdminEmployeeRole);
          setMiscTaskApprovalConfigs([]);
          if (data.employeeAccess) {
            // Employee-only (TAS-667): no admin shell. The portal arrives in Phase 3.
            const self = data.selfEmployee;
            const displayName = self
              ? `${self.firstName} ${self.lastName}`.trim()
              : adminEmployee
                ? `${adminEmployee.name?.first ?? ''} ${adminEmployee.name?.last ?? ''}`.trim()
                : '';
            setUserName(displayName);
            setUserID(adminEmployee?.adminEmployeeID ?? 0);
            // Employee-only: always the employee experience, landing on Today.
            setExperience('employee', false);
            if (portalEnabled) setPage(Page.MyToday);
            setEmployeeOnly(true);
          } else {
            setEmployeeOnly(false);
            setLoginError('Your account does not have Admin App access.');
          }
          setLoading(false);
          return;
        }

        setEmployeeOnly(false);
        const id = adminEmployee.adminEmployeeID;

        if (role.supervisor) {
          setSupervisorQueryID(adminEmployee.employeeID);
          if (adminEmployee.altEmployeeID) {
            setSuperAltID(adminEmployee.altEmployeeID.toString());
          }
        }

        const name = `${adminEmployee.name?.first ?? ''} ${adminEmployee.name?.middle || ''} ${adminEmployee.name?.last ?? ''}`;
        setUserRole(role);
        console.log("User Role:", role);
        setUserID(id);
        setUserName(name);

        // Admin + employee roles: the last experience this user chose (default admin). A deep
        // link names an admin page, so it opens the admin experience (without remembering it).
        const flags = { adminAccess: true, employeeAccess, portalEnabled };
        const experience = pendingDeepLink?.page
          ? 'admin'
          : resolveExperience(flags, readStoredExperience(safeStorage('local'), id));
        setExperience(experience, false);
        setLoggedIn(true);

        if (experience === 'employee') {
          setPage(Page.MyToday);
        } else if (isLimitedUser(role)) {
          const defaultPage = getDefaultPageForLimitedUser(role, config);
          setPage(defaultPage);
        }

        setMiscTaskApprovalConfigs(data.miscTaskApprovalConfigs ?? []);

        // Fetch locations
        try {
          const locationsResponse = await LocationsManager.getLocations();
          setLocations(locationsResponse || []);
        } catch (error) {
          console.error("Error fetching locations:", error);
          setLocations([]);
        }

        // Load pending time off count (for badge). Scope-correct server-side now;
        // gate the fetch on the ViewTimeOffRequests permission (spec §15.3.13).
        if (config.showTimeOffApprovalPage && canPermission(role, 'viewTimeOffRequests')) {
          getPendingTimeOffCount()
            .then((count) => setPendingTimeOffCount(count))
            .catch(() => setPendingTimeOffCount(0));
        }
      } else {
        console.error('No accounts found or MSAL instance is uninitialized.');
      }
    } catch (error) {
      console.error('Error logging in:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      clearSessionState();
      setLoginError(errorMessage || 'Sign-in failed. Please try again.');
      setError("Authentication failed. Please try again.");
    }
    setLoading(false);
  };

  const handleLogin = async () => {
    setLoading(true);
    try {
      if (instance) {
        const loginResponse = await instance.loginPopup({
          scopes: config.loginRequest.scopes,
          redirectUri: config.msalConfig.auth.redirectUri,
        });
        console.log("Login successful:", loginResponse);
        // Pin the account just signed in with, so token requests (api/client.ts) use it
        // rather than whichever cached Microsoft account comes first.
        if (loginResponse?.account) instance.setActiveAccount(loginResponse.account);
        await loginAsync();
      } else {
        console.error("MSAL instance is not initialized properly.");
      }
    } catch (e) {
      console.error('Login failed:', e);
      setError("Login failed. Please try again.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAuthenticated && !loggedIn) {
      loginAsync();
    }
  }, [isAuthenticated, loggedIn, idleNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  // Consume pending deep link after successful login
  useEffect(() => {
    if (loggedIn && pendingDeepLink) {
      if (pendingDeepLink.page) {
        setPage(pendingDeepLink.page);
      }
      setPendingDeepLink(null);
    }
  }, [loggedIn, pendingDeepLink]); // eslint-disable-line react-hooks/exhaustive-deps

  // Spinner while an authenticated user is being signed in. A settled outcome
  // (employee-only card, or an error on the sign-in card) must not spin forever.
  useEffect(() => {
    if (isAuthenticated && !loggedIn && !employeeOnly && !loginError) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, loggedIn, employeeOnly, loginError]); // eslint-disable-line react-hooks/exhaustive-deps

  const can = (permission: AdminPermission): boolean => canPermission(userRole, permission);

  return { loggedIn, employeeOnly, loginError, isAuthenticated, handleLogin, handleLogout, can };
}

export default useAdminSession;
